<template>
  <Teleport to="body">
    <transition name="fade">
      <div v-if="visible" class="security-modal__backdrop">
        <section class="security-modal">
          <header class="security-modal__header">
            <h3 class="security-modal__title">{{ t('wallet.security.title') }}</h3>
            <button
              type="button"
              class="security-modal__close"
              :disabled="submitting"
              @click="handleClose"
            >
              <i class="zmdi zmdi-close" />
            </button>
          </header>

          <div class="security-modal__body">
            <div v-if="loading" class="security-modal__loading">
              <div class="security-modal__spinner" />
              <p>{{ t('wallet.security.initializingSession') }}</p>
            </div>

            <div v-else-if="error" class="security-modal__error">
              <i class="zmdi zmdi-alert-circle-o" />
              <p>{{ error }}</p>
              <button class="security-modal__btn-secondary" @click="initSession()">{{ t('wallet.security.retry') }}</button>
            </div>

            <!-- Спринт 14: пока канал не выбран, сессии действия ещё нет -->
            <OtpChannelChoice
              v-else-if="choiceChannels.length"
              :channels="choiceChannels"
              :busy="submitting"
              @pick="handleChoicePick"
            />

            <div v-else-if="sessionReady" class="security-modal__content">
              <p class="security-modal__text">
                {{ getReceiverText() }}
              </p>
              <p class="security-modal__session" v-if="session">{{ t('wallet.security.session') }}: {{ session }}</p>

              <div class="security-modal__code-input">
                <input
                  v-for="(_, index) in codeLength"
                  :key="index"
                  ref="inputs"
                  type="text"
                  inputmode="numeric"
                  maxlength="1"
                  v-model="codeDigits[index]"
                  @input="handleInput($event, index)"
                  @keydown.delete="handleDelete(index)"
                  @paste.stop.prevent="handlePaste"
                  class="security-modal__digit"
                  autocomplete="one-time-code"
                  :disabled="submitting || codeBurned || suspended"
                />
              </div>
              <label v-if="payPasswordRequired" class="security-modal__password-label">
                {{ t('wallet.payPassword') }}
                <input
                  v-model="paymentPassword"
                  type="password"
                  class="security-modal__payment-password"
                  :placeholder="t('wallet.enterPassword')"
                  :disabled="submitting || suspended || codeBurned || codeThrottled"
                  :aria-invalid="!!paymentPasswordError"
                  autocomplete="current-password"
                />
              </label>
              <p
                v-if="paymentPasswordError"
                role="status"
                class="security-modal__limit"
                data-test="payment-password-error"
              >{{ paymentPasswordError }}</p>
              <p
                v-if="limitMessage"
                role="status"
                class="security-modal__limit"
                data-test="code-limit-error"
              >{{ limitMessage }}</p>
              <p v-else-if="submitError" role="status" class="security-modal__limit">{{ submitError }}</p>
              <!-- 32: код сожжён — новая сессия действия пришлёт новый код -->
              <button
                v-if="codeBurned && !codeThrottled"
                type="button"
                class="security-modal__btn-secondary"
                data-test="request-new-code"
                @click="initSession()"
              >
                {{ t('auth.codeLimits.requestNew') }}
              </button>
            </div>
          </div>

          <footer class="security-modal__footer" v-if="!loading && !error && sessionReady">
            <button class="security-modal__btn-secondary" :disabled="submitting" @click="handleClose">{{ t('wallet.security.cancel') }}</button>
            <button 
              class="security-modal__btn-primary" 
              :disabled="!isComplete || submitting || suspended || codeBurned || codeThrottled"
              @click="handleConfirm"
            >
              <i v-if="submitting" class="zmdi zmdi-spinner zmdi-hc-spin mr-2"/>
              {{ submitting ? t('wallet.security.verifying') : t('wallet.security.confirm') }}
            </button>
          </footer>
        </section>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, reactive, watch, nextTick, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { httpClient } from '@/shared/api/httpClient'
import { parseWalletResponse } from '@/modules/wallet/api/parseWalletResponse'
import OtpChannelChoice from '@/modules/auth/components/OtpChannelChoice.vue'
import type { SecuritySessionResponse } from '@/modules/wallet/types/wallet'
import { useCodeLimit } from '@/composables/useCodeLimit'
import { parseApiError } from '@/shared/utils/apiErrorParser'

interface Props {
  visible: boolean
  actionName: string
  submitting?: boolean
  suspended?: boolean
}

interface Emits {
  (e: 'close'): void
  (e: 'confirm', code: string, paypass?: string): void
}

const props = withDefaults(defineProps<Props>(), { suspended: false })
const emit = defineEmits<Emits>()
const { t } = useI18n()
// Лимиты кодов: 32 — код сожжён (поле закрыто до нового), 33 — повтор закрыт на 15 минут.
const { isBurned: codeBurned, isThrottled: codeThrottled, apply: applyCodeLimit, reset: resetCodeLimit } = useCodeLimit()
const limitMessage = ref('')
const submitError = ref('')
const paymentPassword = ref('')
const paymentPasswordError = ref('')
const payPasswordRequired = ref(false)
let confirmEmitted = false

let generation = 0
let initPending = false
const loading = ref(false)
const error = ref<string | null>(null)
const session = ref<string>('')
const receiver = ref<string>('')
const receiverAddress = ref('')
const maskedEmail = computed(() => {
  const match = /^([^@\s]+)@([^@\s]+\.[^@\s]+)$/.exec(receiverAddress.value)
  if (!match) return ''
  const local = Array.from(match[1])
  return `${local[0]}${'*'.repeat(Math.max(3, local.length - 2))}${local.length > 1 ? local[local.length - 1] : ''}@${match[2]}`
})
// Спринт 14: бэкенд вернул фазу выбора — сессия действия ещё не заведена.
const choiceChannels = ref<string[]>([])
const chosenChannel = ref<'telegram' | 'email'>()
const sessionReady = computed(() => !choiceChannels.value.length && Boolean(session.value || receiver.value === '2fa'))
const canEnterCode = computed(() => props.visible && !props.suspended && sessionReady.value && !loading.value && !error.value && !props.submitting && !codeBurned.value && !codeThrottled.value)
const codeLength = computed(() => receiver.value === '2fa' ? 6 : 7)
const codeDigits = reactive<string[]>(new Array(7).fill(''))
const inputs = ref<HTMLInputElement[]>([])

const isComplete = computed(() => codeDigits.length === codeLength.value && codeDigits.every(d => /^\d$/.test(d)) && (!payPasswordRequired.value || paymentPassword.value.length > 0))
watch(() => [codeDigits.join(''), paymentPassword.value], () => { confirmEmitted = false }, { flush: 'sync' })
watch(paymentPassword, () => { paymentPasswordError.value = '' })

const getReceiverText = () => {
  switch (receiver.value) {
    case 'email':
      return [t('wallet.security.receiverMessages.email'), maskedEmail.value].filter(Boolean).join(' · ')
    case 'telegram':
      return t('wallet.security.receiverMessages.telegram')
    case '2fa':
      return t('wallet.security.receiverMessages.2fa')
    default:
      return t('wallet.security.receiverMessages.default')
  }
}

const initSession = async (channel = chosenChannel.value) => {
  if (loading.value || props.submitting) return
  const attempt = generation
  loading.value = true
  error.value = null
  session.value = ''
  receiver.value = ''
  receiverAddress.value = ''
  codeDigits.fill('')
  submitError.value = ''
  paymentPassword.value = ''
  paymentPasswordError.value = ''
  confirmEmitted = false
  
  try {
    const body: Record<string, string> = { actionName: props.actionName }
    if (channel) body.channel = channel

    // Using postRaw to access response headers (for email-code in dev mode)
    const { data: response } = await httpClient.postRaw<SecuritySessionResponse>('/security/init-action-session', body)
    
    if (attempt !== generation) return
    const parsed = parseWalletResponse<SecuritySessionResponse>(response)
    if (parsed.ok) {
      const data = parsed.data ?? response
      if (data.pay_password_required !== undefined) payPasswordRequired.value = data.pay_password_required
      // Спринт 14: код не отправлен — сперва спрашиваем, куда его прислать
      if (data.choose) {
        choiceChannels.value = data.choose.channels
        return
      }

      choiceChannels.value = []
      session.value = data.session ?? ''
      receiver.value = data.reciever ?? 'email'
      codeDigits.splice(0, codeDigits.length, ...new Array(codeLength.value).fill(''))
      receiverAddress.value = data.reciever_address ?? ''
      resetCodeLimit()
      limitMessage.value = ''
      nextTick(() => inputs.value[0]?.focus())
    } else {
      error.value = t('wallet.security.errors.sessionFailed')
    }
  } catch (failure: unknown) {
    if (attempt !== generation) return
    error.value = parseApiError(failure).message
  } finally {
    if (attempt === generation) loading.value = false
  }
}

/** Человек выбрал канал — заводим сессию действия и шлём код туда. */
const handleChoicePick = async (channel: 'telegram' | 'email'): Promise<void> => {
  if (loading.value || props.submitting || !choiceChannels.value.includes(channel)) return
  chosenChannel.value = channel
  await initSession(channel)
}

const handleInput = (event: Event, index: number) => {
  if (!canEnterCode.value) return
  const input = event.target as HTMLInputElement
  const val = input.value

  // Ensure only numbers
  if (!/^\d*$/.test(val)) {
    codeDigits[index] = ''
    return
  }

  if (val.length === 1 && index < codeLength.value - 1) {
    inputs.value[index + 1]?.focus()
  } else if (val.length > 1) {
    // If somehow multiple chars get in (autocomplete), take the last one
    codeDigits[index] = val.slice(-1)
  }
}

const handleDelete = (index: number) => {
  if (!canEnterCode.value) return
  if (codeDigits[index]) {
    codeDigits[index] = ''
    return
  }
  if (index > 0) {
    codeDigits[index - 1] = ''
    inputs.value[index - 1]?.focus()
  }
}

const handlePaste = (event: ClipboardEvent) => {
  if (!canEnterCode.value) return
  const paste = event.clipboardData?.getData('text')
  if (!paste) return
  
  const numbers = paste.match(/\d/g)
  if (numbers) {
    numbers.slice(0, codeLength.value).forEach((num, i) => {
      codeDigits[i] = num
    })
    inputs.value[Math.min(numbers.length, codeLength.value - 1)]?.focus()
    
    if (!payPasswordRequired.value && numbers.length >= codeLength.value) {
      nextTick(() => handleConfirm())
    }
  }
}

const handleConfirm = () => {
  if (canEnterCode.value && isComplete.value && !confirmEmitted) {
    confirmEmitted = true
    if (payPasswordRequired.value) emit('confirm', codeDigits.join(''), paymentPassword.value)
    else emit('confirm', codeDigits.join(''))
  }
}

const handleClose = () => {
  if (props.submitting) return
  resetSession()
  emit('close')
}

// Global keyboard handler for digit input
const handleGlobalKeydown = (event: KeyboardEvent) => {
  // До выбора канала кода и сессии ещё нет.
  if (!canEnterCode.value) return
  if ((event.target instanceof HTMLInputElement && event.target.type === 'password') || (document.activeElement instanceof HTMLInputElement && document.activeElement.type === 'password')) {
    const target = event.target instanceof HTMLInputElement ? event.target : document.activeElement
    if (event.key === 'Enter' && target?.closest('.security-modal')) {
      event.preventDefault()
      handleConfirm()
    }
    return
  }
  
  // Don't intercept if user is focused on a different input outside modal
  const activeElement = document.activeElement
  const isInputElement = activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement
  const isModalElement = activeElement?.closest('.security-modal')
  
  // Allow if focused in modal or no other input is focused
  if (isInputElement && !isModalElement) return
  
  // Handle digit keys (0-9)
  if (/^[0-9]$/.test(event.key)) {
    event.preventDefault()
    // Find first empty slot
    const emptyIndex = codeDigits.findIndex(d => d === '')
    if (emptyIndex !== -1) {
      codeDigits[emptyIndex] = event.key
      // Focus next input or stay on last if all filled
      const nextIndex = Math.min(emptyIndex + 1, codeLength.value - 1)
      inputs.value[nextIndex]?.focus()
      
      // Auto-submit if this was the last digit
      if (!payPasswordRequired.value && emptyIndex === codeLength.value - 1) {
        nextTick(() => handleConfirm())
      }
    }
  }
  
  // Handle Backspace - delete last filled digit
  if (event.key === 'Backspace') {
    // Find last filled slot
    const lastFilledIndex = codeDigits.reduce((acc, d, i) => d !== '' ? i : acc, -1)
    if (lastFilledIndex !== -1) {
      event.preventDefault()
      codeDigits[lastFilledIndex] = ''
      inputs.value[lastFilledIndex]?.focus()
    }
  }
  
  // Handle Enter - submit if complete
  if (event.key === 'Enter' && isComplete.value && !props.submitting) {
    event.preventDefault()
    handleConfirm()
  }
}

// Global paste handler for Ctrl+V / Cmd+V
const handleGlobalPaste = (event: ClipboardEvent) => {
  if (!canEnterCode.value) return
  if ((event.target instanceof HTMLInputElement && event.target.type === 'password') || (document.activeElement instanceof HTMLInputElement && document.activeElement.type === 'password')) return
  
  // Don't intercept if user is focused on a different input outside modal
  const activeElement = document.activeElement
  const isInputElement = activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement
  const isModalElement = activeElement?.closest('.security-modal')
  
  // Allow if focused in modal or no other input is focused
  if (isInputElement && !isModalElement) return
  
  const paste = event.clipboardData?.getData('text')
  if (!paste) return
  
  const numbers = paste.match(/\d/g)
  if (numbers) {
    event.preventDefault()
    handlePaste(event)
  }
}

// Clear code inputs - exposed for parent to call on error
const clearCode = () => {
  confirmEmitted = false
  codeDigits.fill('')
  nextTick(() => inputs.value[0]?.focus())
}

/**
 * Родитель отдаёт сюда ошибку проверки кода. Лимиты (32, 33) окно показывает у поля
 * само и возвращает true; остальные ошибки остаются родителю.
 */
const showCodeError = (failure: unknown): boolean => {
  confirmEmitted = false
  const message = applyCodeLimit(failure)
  if (!message) return false
  limitMessage.value = message
  if (codeBurned.value) codeDigits.fill('')
  return true
}

/** Opt-in final-submit error display; existing callers can keep handling ordinary errors themselves. */
const showPaymentPasswordError = (message: string): void => {
  confirmEmitted = false
  submitError.value = ''
  paymentPasswordError.value = message
}

const showError = (failure: unknown): void => {
  if (showCodeError(failure)) {
    submitError.value = ''
    return
  }
  const parsed = parseApiError(failure)
  if (parsed.fieldErrors.paypass) showPaymentPasswordError(parsed.fieldErrors.paypass)
  else {
    const walletError = failure instanceof Error ? null : parseWalletResponse(failure)
    submitError.value = parsed.fieldErrors.confirmActionCode || (walletError && !walletError.ok && walletError.message) || parsed.message
  }
}

defineExpose({ clearCode, showCodeError, showError, showPaymentPasswordError })

// Add/remove global keyboard and paste listeners
onMounted(() => {
  window.addEventListener('keydown', handleGlobalKeydown)
  window.addEventListener('paste', handleGlobalPaste)
})

onUnmounted(() => {
  resetSession()
  window.removeEventListener('keydown', handleGlobalKeydown)
  window.removeEventListener('paste', handleGlobalPaste)
})

const resetSession = () => {
  generation++
  initPending = false
  loading.value = false
  session.value = ''
  receiver.value = ''
  receiverAddress.value = ''
  codeDigits.splice(0, codeDigits.length, ...new Array(7).fill(''))
  paymentPassword.value = ''
  paymentPasswordError.value = ''
  payPasswordRequired.value = false
  confirmEmitted = false
  error.value = null
  choiceChannels.value = []
  chosenChannel.value = undefined
  limitMessage.value = ''
  submitError.value = ''
  if (codeBurned.value) resetCodeLimit()
}

watch(() => [props.visible, props.actionName], () => {
  resetSession()
  initPending = props.visible && !!props.submitting
  if (props.visible) initSession()
})

watch(() => props.submitting, (submitting) => {
  if (!submitting) confirmEmitted = false
  if (!submitting && props.visible && initPending) {
    initPending = false
    void initSession()
  }
})
</script>

<style scoped lang="scss">
.security-modal__backdrop {
  position: fixed;
  inset: 0;
  background: var(--ds-scrim);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2100; // Higher than Swal
}

.security-modal {
  width: min(420px, 90%);
  background: var(--ds-surface);
  border-radius: var(--ds-radius-modal);
  box-shadow: 0 24px 48px var(--ds-scrim);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideIn 0.3s ease;
}

.security-modal__header {
  padding: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--hm-border);
}

.security-modal__title {
  margin: 0;
  font-size: var(--p-text-18);
  font-weight: var(--ds-weight-semibold);
  color: var(--hm-text-primary);
}

.security-modal__close {
  background: none;
  border: none;
  color: var(--hm-text-muted);
  font-size: var(--ds-text-size-h-md);
  cursor: pointer;
  padding: 0;
  line-height: 1;
  transition: color 0.2s;
  
  &:hover {
    color: var(--hm-text-primary);
  }
}

.security-modal__body {
  padding: 32px 24px;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
}

.security-modal__text {
  font-size: var(--p-text-15);
  color: var(--hm-text-secondary);
  margin: 0 0 12px;
  line-height: 1.5;
}

.security-modal__session {
  font-size: var(--p-text-13);
  color: var(--hm-text-muted);
  margin: 0 0 24px;
  background: var(--hm-surface-alt);
  padding: 4px 12px;
  border-radius: var(--p-radius-20);
  display: inline-block;
}

.security-modal__code-input {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.security-modal__digit {
  width: 48px;
  height: 56px;
  border: 1px solid var(--hm-border);
  border-radius: var(--ds-radius-card);
  text-align: center;
  font-size: var(--ds-text-size-h-md);
  font-weight: var(--ds-weight-semibold);
  color: var(--hm-text-primary);
  background: var(--hm-surface);
  transition: all 0.2s;
  background: var(--ds-surface-canvas);

  &:focus {
    outline: none;
    border-color: var(--hm-accent);
    background: var(--ds-surface);
    box-shadow: 0 0 0 4px var(--hm-accent-soft);
  }
}

.security-modal__password-label {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 20px;
  text-align: left;
  color: var(--hm-text-primary);
}

.security-modal__payment-password {
  padding: 12px 16px;
  border: 1px solid var(--hm-border);
  border-radius: var(--ds-radius-card);
  color: var(--hm-text-primary);
  background: var(--hm-surface-alt);

  &:focus {
    outline: 2px solid var(--hm-accent);
  }
}

.security-modal__footer {
  padding: 20px 24px;
  border-top: 1px solid var(--hm-border);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.security-modal__btn-secondary,
.security-modal__btn-primary {
  padding: 12px 20px;
  border-radius: var(--p-radius-50);
  font-weight: var(--ds-weight-semibold);
  font-size: var(--ds-text-size-body);
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.security-modal__btn-secondary {
  background: var(--hm-surface-alt);
  color: var(--hm-text-secondary);
  
  &:hover {
    background: var(--hm-border);
  }
}

.security-modal__btn-primary {
  background: var(--hm-accent);
  color: white;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    background: var(--ds-accent);
  }
}

.security-modal__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: var(--hm-text-secondary);
}

.security-modal__spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--hm-border);
  border-top-color: var(--hm-accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.security-modal__error {
  color: var(--hm-danger);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  
  i {
    font-size: var(--p-text-48);
    opacity: 0.5;
  }
}

@keyframes slideIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

// Mobile responsive styles
@media (max-width: 480px) {
  .security-modal__code-input {
    gap: 6px;
  }

  .security-modal__digit {
    width: 36px;
    height: 44px;
    font-size: var(--ds-text-size-h-sm);
    border-radius: var(--p-radius-lg);
  }
}
.security-modal__limit {
  margin: 12px 0 0;
  color: var(--ds-danger);
  text-align: center;
}
</style>
